// src/lib/shiftPrompt.js — when does the shift-start prompt appear, and what may it offer?
//
// Owner, need-care list 2026-09-02: "at the start of their shift they must be notified are you
// at the office if yes select it."
//
// PURE on purpose. The component that draws this banner lives behind a login, in the app shell,
// on a clock — three things that make it expensive to observe and easy to get wrong in ways no
// build step can see. Every "should it show / what may it offer" question is answered here from
// plain values, so the tests can ask about a 09:00 shift at 08:59 without a browser, a session,
// or waiting until nine.
//
// ⚠️ THIS DECIDES WHAT IS OFFERED, NEVER WHAT IS ALLOWED. mark_own_arrival resolves the agent
// from the session and the location from the edge (0002); attendance writes are management-only
// (0003). A wrong answer here shows a wrong button, and the database still refuses it.

/** The banner is silent unless there is something true to say. */
export const SILENT = null

/**
 * @param {object} o
 * @param {Array}  o.slots     today's scheduled slots, [{start:'09:00', end:'18:00'}, …]
 * @param {string} o.nowHM     wall clock in the office timezone, 'HH:MM'
 * @param {?string} o.checkIn  the arrival already recorded today, or null
 * @param {boolean} o.loading  true while today's row has not been read yet
 * @param {boolean} o.failed   true when reading today's row failed
 * @param {?object} o.loc      the location verdict {ok, reason}, or null while it is unknown
 * @returns {null | {mode:'arrive'|'blocked', start:string, end:string, reason:?string}}
 */
export function shiftPromptState({ slots = [], nowHM = '', checkIn = null, loading = false, failed = false, loc = null } = {}) {
  // ⚠️ NEVER CLAIM AN ABSENCE WE HAVE NOT READ. While the row is loading, or if the read
  // failed, "you have not marked your arrival" is a guess — and it is the guess that produces a
  // second press over a row that already exists.
  if (loading || failed) return SILENT

  // The press has landed. This is the banner's whole purpose, so it leaves immediately.
  if (checkIn) return SILENT

  const usable = (slots || []).filter(s => s && s.start && s.end)
  if (usable.length === 0) return SILENT          // no shift today: nothing to be late for

  // ⚠️ FIRST START AND LAST END, not slot-by-slot. A split shift (09–13, 16–20) is ONE working
  // day with ONE arrival: prompting again at 16:00 would ask for a second press that
  // mark_own_arrival is written to refuse, and the refusal would read as a bug.
  const start = String(usable[0].start).slice(0, 5)
  const end   = String(usable[usable.length - 1].end).slice(0, 5)
  if (!/^\d\d:\d\d$/.test(start) || !/^\d\d:\d\d$/.test(end)) return SILENT

  // 'HH:MM' strings compare correctly as strings, which is why the slice above is not optional.
  if (nowHM < start) return SILENT                // the shift has not started
  // ⚠️ IT STOPS AT THE END OF THE SHIFT, and that is the only thing stopping it. There is no
  // dismiss button (a control whose only function is to hide a true statement is how a feature
  // quietly stops working), so this bound is what keeps it from nagging into the night.
  // Marking an arrival after the shift is over is a correction, and corrections are a manager's.
  if (nowHM > end) return SILENT

  // A verdict that has NOT ARRIVED must not block: `loc === null` means we have not asked yet,
  // or the ask failed, and a slow query must never present as a refusal. Only an explicit
  // `ok === false` withholds the button — and then it names the reason and offers a re-check,
  // because a lock has to name an action the viewer actually has.
  if (loc && loc.ok === false) {
    return { mode: 'blocked', start, end, reason: loc.reason || null }
  }
  return { mode: 'arrive', start, end, reason: null }
}
